import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDocs, collection, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';

const ITEMS_PER_PAGE = 10;

const STATUSES = [
  { id: 'PENDING', color: '#64748b', bg: '#f1f5f9' },
  { id: 'APPROVED_BY_OLD', color: '#0891b2', bg: '#ecfeff' },
  { id: 'APPROVED_BY_NEW', color: '#7c3aed', bg: '#f5f3ff' },
  { id: 'COMPLETED', color: '#16a34a', bg: '#f0fdf4' },
  { id: 'REJECTED', color: '#dc2626', bg: '#fef2f2' },
];

export default function ManageTransfers() {
  const t = useT();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('PENDING');
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      if (DB_TYPE === 'firebase') {
        const snap = await getDocs(collection(db, 'branch_transfers'));
        setTransfers(snap.docs.map(d => ({ _id: d.id, ...d.data() })));
      } else {
        const r = await api.get('/transfers');
        setTransfers(r.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      if (DB_TYPE === 'firebase') {
        await updateDoc(doc(db, 'branch_transfers', id), { status, updatedAt: new Date().toISOString() });
        // If COMPLETED, we should also update the member's current branch
        if (status === 'COMPLETED') {
            const transfer = transfers.find(t => t._id === id);
            if (transfer) {
                await updateDoc(doc(db, 'members', transfer.memberId), { current_branch_id: transfer.newBranchId });
            }
        }
      } else {
        await api.patch(`/transfers/${id}`, { status });
      }
      toast.success(t('statusUpdated') || 'Status updated');
      load();
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const filtered = transfers.filter(tr => tr.status === tab);

  const isRTL = t.language === 'sindhi';

  const getStatusLabel = (statusId) => {
    if (!isRTL) return statusId.replace(/_/g, ' ');
    switch (statusId) {
      case 'PENDING': return 'التوا ۾';
      case 'APPROVED_BY_OLD': return 'پراڻي شاخ منظور ڪئي';
      case 'APPROVED_BY_NEW': return 'نئين شاخ منظور ڪئي';
      case 'COMPLETED': return 'مڪمل ٿيل';
      case 'REJECTED': return 'مسترد';
      default: return statusId;
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {t('transferRequests')}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {t('manageMemberTransfers')}
          </p>
        </div>
        <div style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
          {filtered.length} {isRTL ? 'درخواستون' : 'requests'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 24, background: 'var(--sindhi-cream)', padding: 6, borderRadius: 18, width: 'fit-content', flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr' }}>
        {STATUSES.map(s => (
          <button 
            key={s.id} 
            className={`btn-tab ${tab === s.id ? 'active' : ''}`} 
            style={{ 
                padding: '10px 22px', 
                borderRadius: 14, 
                border: 'none', 
                fontSize: 13, 
                fontWeight: 700, 
                cursor: 'pointer', 
                background: tab === s.id ? 'white' : 'transparent', 
                color: tab === s.id ? 'var(--sindhi-navy)' : 'var(--sindhi-text-muted)', 
                boxShadow: tab === s.id ? '0 4px 12px rgba(0,0,0,0.08)' : 'none', 
                transition: 'all 0.2s',
                fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit'
            }} 
            onClick={() => { setTab(s.id); setPage(1); }}
          >
            {getStatusLabel(s.id)}
            {transfers.filter(tr => tr.status === s.id).length > 0 && (
                <span style={{ [isRTL ? 'marginRight' : 'marginLeft']: 10, background: 'var(--sindhi-maroon)', color: 'white', borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 800 }}>
                    {transfers.filter(tr => tr.status === s.id).length}
                </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}><div className="spinner" /></div>
      ) : (
        <Card style={{ overflow: 'hidden', border: '1px solid var(--sindhi-cream)', background: 'white' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textTransform: 'uppercase' }}>{t('member')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textTransform: 'uppercase' }}>{t('transferPath')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textTransform: 'uppercase' }}>{t('currentStatus')}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textTransform: 'uppercase' }}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {filtered.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan={4} style={{ textAlign: 'center', padding: 80, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        {t('noRequestsFound')}
                      </td>
                    </motion.tr>
                  ) : (
                    filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(tr => (
                      <motion.tr key={tr._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ borderBottom: '1px solid var(--sindhi-cream)' }}>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', fontSize: isRTL ? 18 : 15 }}>{isRTL ? (tr.memberNameSindhi || tr.memberName) : tr.memberName}</div>
                          <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)' }}>ID: {tr.memberId}</div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexDirection: isRTL ? 'row-reverse' : 'row', justifyContent: isRTL ? 'flex-end' : 'flex-start' }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (tr.oldBranchNameSindhi || tr.oldBranchName) : tr.oldBranchName}</span>
                            <span style={{ opacity: 0.5, fontSize: 18, fontWeight: 800, color: 'var(--sindhi-maroon)' }}>{isRTL ? '←' : '→'}</span>
                            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--sindhi-maroon)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (tr.newBranchNameSindhi || tr.newBranchName) : tr.newBranchName}</span>
                          </div>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <span style={{ 
                            padding: '6px 14px', borderRadius: 10, fontSize: 11, fontWeight: 800,
                            color: STATUSES.find(s => s.id === tr.status)?.color,
                            background: STATUSES.find(s => s.id === tr.status)?.bg,
                            fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
                            textTransform: 'uppercase'
                          }}>
                            {getStatusLabel(tr.status)}
                          </span>
                        </td>
                        <td style={{ padding: '20px 24px' }}>
                          <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                            {tr.status === 'PENDING' && (
                              <button onClick={() => updateStatus(tr._id, 'APPROVED_BY_OLD')} className="btn-action" style={{ background: '#0891b2', color: 'white', border: 'none', padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'پراڻي شاخ منظور ڪري' : t('approveOld')}</button>
                            )}
                            {tr.status === 'APPROVED_BY_OLD' && (
                              <button onClick={() => updateStatus(tr._id, 'APPROVED_BY_NEW')} className="btn-action" style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'نئين شاخ منظور ڪري' : t('approveNew')}</button>
                            )}
                            {tr.status === 'APPROVED_BY_NEW' && (
                              <button onClick={() => updateStatus(tr._id, 'COMPLETED')} className="btn-action" style={{ background: '#16a34a', color: 'white', border: 'none', padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'منتقلي مڪمل ڪريو' : t('completeTransfer')}</button>
                            )}
                            {(tr.status === 'PENDING' || tr.status === 'APPROVED_BY_OLD') && (
                              <button onClick={() => updateStatus(tr._id, 'REJECTED')} className="btn-action" style={{ background: '#feecef', color: '#dc2626', border: 'none', padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'مسترد' : t('reject')}</button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div style={{ padding: 24 }}>
            <Pagination page={page} totalPages={Math.ceil(filtered.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </Card>
      )}
    </motion.div>
  );
}
